import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { AppPicker } from "@/modules/shell/AppPicker";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { listSalesOrders } from "@/shared/api/sales";
import {
  getReturnSource,
  listRecentPosTickets,
  postReturn,
} from "@/shared/api/returns";
import { DocNumberField } from "@/modules/core/components/DocNumberField";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import { apiErrorCode, isApiError } from "@/shared/api/api-error";
import { createOverrideRequest } from "@/shared/api/overrides";
import { newClientIdempotencyKey } from "@/shared/client-idempotency";

export function ReturnNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [searchParams] = useSearchParams();
  const querySourceType = searchParams.get("sourceType");
  const querySourceId = searchParams.get("sourceId");
  const initialType =
    querySourceType === "sales_order" ? "sales_order" : "pos_ticket";
  const [sourceType, setSourceType] = useState<"pos_ticket" | "sales_order">(
    initialType,
  );
  const [sourceId, setSourceId] = useState(querySourceId ?? "");
  const [tickets, setTickets] = useState<
    Array<{ id: string; number: string; grossTotal: string }>
  >([]);
  const [orders, setOrders] = useState<
    Array<{ id: string; number: string; customerName: string }>
  >([]);
  const [lines, setLines] = useState<
    Awaited<ReturnType<typeof getReturnSource>>["lines"]
  >([]);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [lotByLine, setLotByLine] = useState<Record<string, string>>({});
  const [serialByLine, setSerialByLine] = useState<Record<string, string>>({});
  const [restock, setRestock] = useState(true);
  const [settleMode, setSettleMode] = useState<"cash_refund" | "credit">(
    "cash_refund",
  );
  const [overrideRequestId, setOverrideRequestId] = useState<string | null>(
    null,
  );
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [docNumber, setDocNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companySlug) return;
    void Promise.all([
      listRecentPosTickets(companySlug),
      listSalesOrders(companySlug),
    ]).then(([t, o]) => {
      setTickets(t.tickets);
      setOrders(o.orders.filter((x) => x.lifecycle === "open"));
      // Deep-link from POS / SO keeps query source; otherwise default first ticket
      if (querySourceId) {
        setSourceId(querySourceId);
        if (querySourceType === "sales_order" || querySourceType === "pos_ticket") {
          setSourceType(querySourceType);
        }
        return;
      }
      if (t.tickets[0]) setSourceId(t.tickets[0].id);
    });
  }, [companySlug, querySourceId, querySourceType]);

  useEffect(() => {
    if (!companySlug || !sourceId) {
      setLines([]);
      return;
    }
    getReturnSource(companySlug, sourceType, sourceId)
      .then((d) => {
        setLines(d.lines);
        const init: Record<string, string> = {};
        const lots: Record<string, string> = {};
        const serials: Record<string, string> = {};
        for (const l of d.lines) {
          init[l.sourceLineId] = Number(l.qtyOpen) > 0 ? l.qtyOpen : "0";
          if (l.lotCode) lots[l.sourceLineId] = l.lotCode;
          if (l.serialCode) serials[l.sourceLineId] = l.serialCode;
        }
        setQty(init);
        setLotByLine(lots);
        setSerialByLine(serials);
        setError(null);
      })
      .catch((err: unknown) => {
        setLines([]);
        setError(err instanceof Error ? err.message : "Failed");
      });
  }, [companySlug, sourceType, sourceId]);

  if (!companySlug) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = lines
        .map((l) => ({
          sourceLineId: l.sourceLineId,
          qty: qty[l.sourceLineId] ?? "0",
          ...(l.trackLot && lotByLine[l.sourceLineId]?.trim()
            ? { lotCode: lotByLine[l.sourceLineId]!.trim() }
            : {}),
          ...(l.trackSerial && serialByLine[l.sourceLineId]?.trim()
            ? { serialCode: serialByLine[l.sourceLineId]!.trim() }
            : {}),
        }))
        .filter((l) => Number(l.qty) > 0);
      if (payload.length === 0) throw new Error("Enter qty to return");
      const base = {
        sourceType,
        sourceId,
        restock,
        ...(restock && warehouseId ? { warehouseId } : {}),
        settleMode:
          sourceType === "sales_order" ? settleMode : "cash_refund",
        lines: payload,
        ...(docNumber.trim() ? { number: docNumber.trim() } : {}),
        overrideRequestId: overrideRequestId ?? undefined,
      };
      /** One key for soft-confirm retry — plan/24 */
      const idempotencyKey = newClientIdempotencyKey("ret");
      let result: Awaited<ReturnType<typeof postReturn>>;
      try {
        result = await postReturn(companySlug!, {
          ...base,
          idempotencyKey,
        });
      } catch (err: unknown) {
        if (!isApiError(err) || apiErrorCode(err) !== "over_return") {
          throw err;
        }
        const ok = await confirmUi({
          title: "Over-return?",
          description: `${err.message}\n\nReturn anyway? Extra qty may restock beyond what was sold.`,
          confirmLabel: "Return anyway",
          destructive: true,
        });
        if (!ok) {
          setError("Return cancelled");
          return;
        }
        result = await postReturn(companySlug!, {
          ...base,
          confirmOverReturn: true,
          idempotencyKey,
        });
      }
      if (result.glError) {
        setError(`Posted ${result.return.number} but GL: ${result.glError}`);
      }
      navigate(`/c/${companySlug}/returns/${result.return.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onRequestReturnOverride() {
    if (!companySlug) return;
    setBusy(true);
    setError(null);
    const base = {
      permissionKey: "pos.return",
      summary: `Return ${sourceType} ${sourceId.slice(0, 8)}`,
      grantScope: "transaction" as const,
    };
    try {
      let request: { id: string; status: string };
      try {
        ({ request } = await createOverrideRequest(companySlug, {
          ...base,
          autoApprove: true,
        }));
      } catch {
        ({ request } = await createOverrideRequest(companySlug, base));
      }
      setOverrideRequestId(request.id);
      setError(
        request.status === "approved"
          ? null
          : "Return override requested — Approvals inbox, then submit again",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Override failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="New return"
        description="Void cancels a mistake · Return = customer brought goods back"
      />

      <form className="mt-8 space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <div>
          <Label htmlFor="return-source-type">Source</Label>
          <Select
            id="return-source-type"
            className="mt-1"
            value={sourceType}
            onChange={(e) => {
              const next = e.target.value as "pos_ticket" | "sales_order";
              setSourceType(next);
              setSourceId(
                next === "pos_ticket"
                  ? (tickets[0]?.id ?? "")
                  : (orders[0]?.id ?? ""),
              );
            }}
          >
            <option value="pos_ticket">POS ticket</option>
            <option value="sales_order">Sales order</option>
          </Select>
        </div>

        <div>
          <Label htmlFor="return-source-id">Document</Label>
          <AppPicker
            id="return-source-id"
            className="mt-1"
            title="Document"
            description={
              sourceType === "pos_ticket"
                ? "Search POS tickets"
                : "Search sales orders"
            }
            placeholder="Select…"
            value={sourceId || null}
            options={
              sourceType === "pos_ticket"
                ? tickets.map((t) => ({
                    value: t.id,
                    label: t.number,
                    description: `₱${t.grossTotal}`,
                    keywords: `${t.number} ${t.grossTotal}`,
                  }))
                : orders.map((o) => ({
                    value: o.id,
                    label: o.number,
                    description: o.customerName,
                    keywords: `${o.number} ${o.customerName}`,
                  }))
            }
            emptyLabel={
              sourceType === "pos_ticket"
                ? "No recent POS tickets"
                : "No open sales orders"
            }
            onChange={(id) => setSourceId(id ?? "")}
          />
        </div>

        <ul className="m-0 list-none space-y-3 p-0">
          {lines.map((l) => (
            <li
              key={l.sourceLineId}
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/60 px-3 py-2.5 text-sm"
            >
              <div className="font-medium">{l.productName}</div>
              <div className="text-xs text-[var(--color-muted)]">
                sold {l.qtySold} · already returned {l.qtyReturned} · open{" "}
                {l.qtyOpen}
              </div>
              {Number(l.qtyOpen) > 0 && (
                <div className="mt-2 space-y-2 text-xs">
                  <label className="flex items-center gap-2">
                    Return qty
                    <Input
                      className="h-9 w-24 font-mono"
                      value={qty[l.sourceLineId] ?? ""}
                      onChange={(e) =>
                        setQty((prev) => ({
                          ...prev,
                          [l.sourceLineId]: e.target.value,
                        }))
                      }
                    />
                  </label>
                  {restock && l.trackLot && (
                    <label className="flex items-center gap-2">
                      Lot #
                      <Input
                        className="h-9 min-w-0 flex-1 font-mono"
                        value={lotByLine[l.sourceLineId] ?? ""}
                        onChange={(e) =>
                          setLotByLine((prev) => ({
                            ...prev,
                            [l.sourceLineId]: e.target.value,
                          }))
                        }
                        placeholder="Required to restock"
                      />
                    </label>
                  )}
                  {restock && l.trackSerial && (
                    <label className="flex items-center gap-2">
                      Serial
                      <Input
                        className="h-9 min-w-0 flex-1 font-mono"
                        value={serialByLine[l.sourceLineId] ?? ""}
                        onChange={(e) =>
                          setSerialByLine((prev) => ({
                            ...prev,
                            [l.sourceLineId]: e.target.value,
                          }))
                        }
                        placeholder="Qty 1 only"
                      />
                    </label>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>

        {restock && (
          <WarehouseSelect
            companySlug={companySlug}
            value={warehouseId}
            onChange={setWarehouseId}
            disabled={busy}
            label="Restock into"
          />
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={restock}
            onChange={(e) => setRestock(e.target.checked)}
          />
          Restock (default on)
        </label>

        <AdvancedSection subtitle="number & settle">
          <DocNumberField
            companySlug={companySlug}
            docType="sales_return"
            value={docNumber}
            onChange={setDocNumber}
          />
          {sourceType === "sales_order" ? (
            <div>
              <Label htmlFor="return-settle">Settle</Label>
              <Select
                id="return-settle"
                className="mt-1"
                value={settleMode}
                onChange={(e) =>
                  setSettleMode(e.target.value as "cash_refund" | "credit")
                }
              >
                <option value="cash_refund">Cash refund</option>
                <option value="credit">Credit on account</option>
              </Select>
            </div>
          ) : (
            <p className="m-0 text-xs text-[var(--color-muted)]">
              POS returns settle as cash refund.
            </p>
          )}
        </AdvancedSection>

        {error && <Alert variant="destructive">{error}</Alert>}

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            disabled={!sourceId}
            pending={busy}
            pendingLabel="Posting…"
          >
            Post return
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void onRequestReturnOverride()}
          >
            {overrideRequestId ? "Override ready" : "Request return override"}
          </Button>
          <Button variant="ghost" asChild>
            <Link to={`/c/${companySlug}/returns`} className="no-underline">
              Cancel
            </Link>
          </Button>
        </div>
        <p className="text-xs text-[var(--color-muted)]">
          Needs <code>pos.return</code> (Cashier has it). Void is a separate
          permission.
        </p>
      </form>
    </PageFrame>
  );
}
